/**
 * Deployable Lead Rate Card (MA-UI-107)
 * 
 * Shows percentage of leads that are eligible for outreach.
 * True usable lead inventory metric.
 */

import React from 'react';
import { Users, TrendingDown, TrendingUp, Info, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

// =============================================================================
// STATUS CONFIG
// =============================================================================

const STATUS_CONFIG = {
  healthy: {
    label: 'Healthy',
    icon: CheckCircle,
    className: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
    borderColor: 'border-l-emerald-500/50',
    progressColor: 'bg-emerald-500',
  },
  watch: {
    label: 'Watch',
    icon: AlertTriangle,
    className: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
    borderColor: 'border-l-amber-500/50',
    progressColor: 'bg-amber-500',
  },
  critical: {
    label: 'Critical',
    icon: XCircle,
    className: 'bg-red-500/10 text-red-400 border-red-500/30',
    borderColor: 'border-l-red-500/50',
    progressColor: 'bg-red-500',
  },
};

// =============================================================================
// DEPLOYABLE LEAD RATE CARD
// =============================================================================

export function DeployableLeadRateCard({ data, isLoading }) {
  // Loading state
  if (isLoading) {
    return (
      <Card className="glass border-white/10 border-l-4 border-l-slate-500/50">
        <CardContent className="p-5">
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-slate-700 rounded w-1/2"></div>
            <div className="h-10 bg-slate-700 rounded w-1/3"></div>
            <div className="h-2 bg-slate-700 rounded w-full"></div>
            <div className="h-3 bg-slate-700 rounded w-3/4"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Empty state
  if (!data) {
    return (
      <Card className="glass border-white/10 border-l-4 border-l-slate-500/50">
        <CardContent className="p-5">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-slate-400" />
              <span className="text-sm font-medium text-slate-300">Deployable Lead Rate</span>
            </div>
            <p className="text-2xl font-bold text-slate-400">—</p>
            <p className="text-xs text-slate-500">
              Not enough lead data to calculate deployable rate.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const { rate, deployable_leads, total_leads, compliance_ready_leads, delta_percent, status } = data;
  
  // MA-AN-111: Handle insufficient compliance data (rate is null)
  if (rate === null || rate === undefined || status === 'insufficient_data') {
    return (
      <Card className="glass border-white/10 border-l-4 border-l-slate-500/50" data-testid="deployable-lead-rate-card">
        <CardContent className="p-5">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-slate-500/10">
                <Users className="h-4 w-4 text-slate-400" />
              </div>
              <span className="text-sm font-medium text-slate-300">Deployable Lead Rate</span>
            </div>
            <p className="text-2xl font-bold text-slate-400">—</p>
            <p className="text-xs text-slate-500">
              {total_leads > 0 
                ? `${total_leads} leads found, but none have compliance eligibility data yet.`
                : 'No funnel leads in this period.'
              }
            </p>
            {compliance_ready_leads !== undefined && compliance_ready_leads < 10 && compliance_ready_leads > 0 && (
              <p className="text-xs text-amber-400/70">
                Only {compliance_ready_leads} leads with compliance data (minimum 10 recommended for reliable metrics).
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }
  
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.watch;
  const StatusIcon = config.icon;
  const ratePercent = (rate * 100);
  const isImproving = delta_percent !== null && delta_percent > 0;

  return (
    <Card 
      className={`glass border-white/10 border-l-4 ${config.borderColor}`}
      data-testid="deployable-lead-rate-card"
    >
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-indigo-500/10">
              <Users className="h-4 w-4 text-indigo-400" />
            </div>
            <span className="text-sm font-medium text-slate-300">Deployable Lead Rate</span>
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Info className="h-3.5 w-3.5 text-slate-500" />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                <p className="text-xs">
                  Deployable Lead Rate shows how much of your lead volume was truly usable 
                  after compliance and eligibility checks.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Primary Metric with Status Badge */}
        <div className="flex items-center gap-3 mb-2">
          <span className="text-3xl font-bold text-slate-100">
            {ratePercent.toFixed(0)}%
          </span>
          
          {/* Status Badge */}
          <Badge 
            variant="outline" 
            className={`text-xs gap-1 ${config.className}`}
          >
            <StatusIcon className="h-3 w-3" />
            {config.label}
          </Badge>
          
          {/* Trend indicator */}
          {delta_percent !== null && delta_percent !== 0 && (
            <div className={`flex items-center gap-0.5 text-xs ${
              isImproving ? 'text-emerald-400' : 'text-red-400'
            }`}>
              {isImproving ? (
                <TrendingUp className="h-3 w-3" />
              ) : (
                <TrendingDown className="h-3 w-3" />
              )}
              <span>{Math.abs(delta_percent).toFixed(1)}%</span>
            </div>
          )}
        </div>

        {/* Helper text */}
        <p className="text-xs text-slate-400 mb-4">
          The percentage of generated leads that were actually eligible for outreach.
        </p>

        {/* Progress bar */}
        <div className="mb-3">
          <Progress 
            value={ratePercent} 
            className={`h-2 bg-slate-700/50`}
          />
        </div>

        {/* Secondary stats */}
        <div className="flex items-center justify-between text-xs text-slate-400">
          <div>
            <span className="text-slate-500">Deployable:</span>{' '}
            <span className="text-slate-300 font-medium">{deployable_leads?.toLocaleString()}</span>
          </div>
          <div>
            <span className="text-slate-500">Evaluated:</span>{' '}
            <span className="text-slate-300 font-medium">{(compliance_ready_leads || total_leads)?.toLocaleString()}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default DeployableLeadRateCard;
